/* eslint-disable @typescript-eslint/no-shadow */
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

import iconAdd from '../../../../assets/icons/add-circle.svg';
import arrowdown from '../../../../assets/icons/arrow-down.svg';
import calendarIcon from '../../../../assets/icons/calendar.svg';
import gallonsIcon from '../../../../assets/icons/capacity.svg';
import locationIcon from '../../../../assets/icons/location.svg';
import truckIcon from '../../../../assets/icons/truck.svg';
import removeIcon from '../../../../assets/icons/trush-square.svg';
import decalIcon from '../../../../assets/icons/vehicle-decal.svg';
import userIcon from '../../../../assets/icons/workers.svg';
import { useNotification } from '../../../../hooks/useNotification';
import { confirmationModalState } from '../../../../recoil/modalDelete';
import { needRefreshState } from '../../../../recoil/needRefresh';
import {
  CREATE_GTW_PAYMENT,
  DELETE_GREASE_TRAP,
  UPDATE_GREASE_TRAP,
} from '../../../../services/graphql/vendors/grease-traps/greaseTrapMutation';
import type {
  GreaseTrap,
  GreaseTrapDropdownOptions,
} from '../../../../types/greaseTrap';
import {
  convertDateToString,
  parseDateWithoutTimezone,
} from '../../../../utils/convertDate';
import { formatNumber } from '../../../../utils/numberFormatter';
import DropdownWithContent from '../../common/DropdownWithContent';
import Dropdown from '../../common/Inputs/Dropdown';
import InputCardField from '../../common/Inputs/InputCardField';
import Toggle from '../../common/Toggle';
import LoaderSmall from '../../LoaderSmall';
import DTGreaseTrapDocuments from './DTGreaseTrapDocuments';
import GreaseTrapNotes from './GreaseTrapNotes';
import { GreaseTrapPaymentCard } from './GreaseTrapPaymentCard';
import { GreaseTrapStatusDropdown } from './GreaseTrapStatusDropdown';

const IconTextElement = ({ icon, text }: { icon: string; text: string }) => {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-x-1">
      <img src={icon} alt="card icon" width="22" height="22" />
      <p className="text-center text-sm font-semibold">{text || '-'}</p>
    </div>
  );
};

type Props = {
  greaseTrap: GreaseTrap;
  vendorName?: string;
  vendorId?: string;
  refetchSingleGreaseTrap: (id: string) => void;
  loadingSingleQuery: boolean;
  dropdownOptions?: GreaseTrapDropdownOptions;
  shadow?: string;
};

export const GreaseTrapCard: React.FC<Props> = ({
  greaseTrap,
  vendorName,
  refetchSingleGreaseTrap,
  loadingSingleQuery,
  dropdownOptions,
  shadow = 'shadow-[0px_2px_12px_0px_rgba(0,0,0,0.20)]',
}) => {
  const { t } = useTranslation('common');
  const { notifySaveError, notifySaveSuccess } = useNotification();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const setConfirmModalState = useSetRecoilState(confirmationModalState);
  const setNeedsRefresh = useSetRecoilState(needRefreshState);

  const { watch, setValue, reset } = useForm<GreaseTrap>({
    defaultValues: {
      ...greaseTrap,
    },
  });

  const d = watch();

  const convertToString = (value: string | number | boolean): string => {
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    return String(value);
  };

  const [createPayment] = useMutation(CREATE_GTW_PAYMENT);
  const [updateGreaseTrap] = useMutation(UPDATE_GREASE_TRAP);
  const [deleteGreaseTrap] = useMutation(DELETE_GREASE_TRAP);

  useEffect(() => {
    const balance = greaseTrap.payments
      .filter((payment) => payment.paid_at)
      .reduce((acc, current) => acc - current.amount, greaseTrap.total_amount);

    reset({
      ...greaseTrap,
      balance,
    });
  }, [greaseTrap, reset]);

  useEffect(() => {
    if (!loadingSingleQuery) {
      setLoading(false);
    }
  }, [loadingSingleQuery]);

  const handleUpdate = (
    key: keyof GreaseTrap,
    value: string | number | boolean,
  ) => {
    const currentValue = greaseTrap[key];

    if (value !== currentValue) {
      setLoading(true);
      setValue(key, value as never);

      updateGreaseTrap({
        variables: {
          id: greaseTrap.id,
          key,
          value: convertToString(value),
        },
        onCompleted: () => {
          refetchSingleGreaseTrap(greaseTrap.id);
          notifySaveSuccess();
        },
        onError: () => {
          notifySaveError();
          setLoading(false);
        },
      });
    }
  };

  const handleNewPayment = () => {
    setLoading(true);

    createPayment({
      variables: {
        greaseTrapId: greaseTrap.id,
      },
      onCompleted: () => {
        refetchSingleGreaseTrap(greaseTrap.id);
        notifySaveSuccess();
        setNeedsRefresh('grease-traps-stats');
        setTimeout(() => {
          setNeedsRefresh(false);
        }, 500);
      },
      onError: () => {
        notifySaveError();
        setLoading(false);
      },
    });
  };

  const handleDeleteGreaseTrap = () => {
    setConfirmModalState({
      isOpen: true,
      onConfirm: () => {
        deleteGreaseTrap({
          variables: { id: greaseTrap.id },
          onCompleted: () => {
            setNeedsRefresh('grease-traps');
            setTimeout(() => {
              setNeedsRefresh(false);
            }, 500);
          },
        });
      },
      onCancel: () => {},
      message: t('global.are-you-sure-delete'),
    });
  };

  return (
    <div
      className={`flex w-full flex-col items-start justify-start gap-[15px] rounded-[10px] bg-white p-2.5 ${shadow}`}
    >
      <div className="mx-auto flex w-full flex-col gap-y-4 text-left">
        <div className="flex flex-row justify-between gap-3">
          <div className="flex w-full items-center text-base font-semibold text-zinc-800">
            {vendorName || greaseTrap.vendor?.name || '-'}
          </div>
          <div className="flex w-full flex-row items-center justify-end gap-2">
            <span className="min-w-[100px] text-right text-sm">
              {parseDateWithoutTimezone(
                greaseTrap.date_service,
              ).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            <GreaseTrapStatusDropdown
              initialStatus={d.status}
              greaseTrapId={greaseTrap.id}
              refetchSingleGreaseTrap={refetchSingleGreaseTrap}
              setLoading={setLoading}
            />
            <button
              onClick={toggleDropdown}
              type="button"
              className="inline-flex h-7 items-center justify-between text-lg font-semibold"
              aria-expanded={isOpen}
            >
              <span
                className={`relative ml-2 h-5 w-5 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              >
                <img src={arrowdown} alt="Arrow Down" width="22" height="22" />
              </span>
            </button>
          </div>
        </div>
        <div
          className={`w-full origin-top-right rounded-md ring-black ${
            loading ? 'block' : 'hidden'
          }`}
        >
          <div className="flex flex-row items-center justify-around">
            <LoaderSmall />
          </div>
        </div>
        <div
          className={`w-full origin-top-right rounded-md ring-black ${
            !isOpen && !loading ? 'block' : 'hidden'
          }`}
        >
          <div className="flex flex-row items-center justify-around gap-4">
            <IconTextElement
              icon={truckIcon}
              text={d.truck?.license_plate || '-'}
            />
            <IconTextElement
              icon={locationIcon}
              text={d.location?.name || '-'}
            />
            <IconTextElement
              icon={decalIcon}
              text={d.vehicle_decal_number || '-'}
            />
            <IconTextElement
              icon={gallonsIcon}
              text={`${formatNumber(d.gallons_grease_received || 0)} Gal`}
            />
            <IconTextElement
              icon={userIcon}
              text={
                (d.registered_by as unknown as { name: string })?.name || '-'
              }
            />
          </div>
        </div>
        <div
          className={`w-full origin-top-right flex-col gap-y-4 rounded-md ring-black ${
            isOpen && !loading ? 'flex' : 'hidden'
          }`}
        >
          <div className="grid w-full grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="flex items-center justify-between gap-2 border-b border-gray-400">
              <div className="truncate text-sm !font-normal text-gray-400">
                {t('vendor-grease-trap-card.location')}
              </div>
              <Dropdown
                options={dropdownOptions?.locationOptions || []}
                selectedValue={d.location_id || ''}
                placeholder={t('vendor-grease-trap-card.location')}
                onSelectChange={(newValue) => {
                  handleUpdate('location_id', newValue);
                }}
                selectClassname="!font-normal"
              />
            </div>
            <div className="flex items-center justify-between gap-2 border-b border-gray-400">
              <div className="truncate text-sm !font-normal text-gray-400">
                {t('vendor-grease-trap-card.date-service')}
              </div>
              <div className="flex items-center justify-end">
                <ReactDatePicker
                  calendarStartDay={1}
                  dateFormat="MM-dd-yyyy"
                  className="w-full cursor-pointer border-none bg-white p-0 px-2 text-right text-xs text-green focus:ring-0 focus:ring-transparent focus:ring-offset-0"
                  selected={parseDateWithoutTimezone(d.date_service)}
                  onChange={(date) => {
                    if (date) {
                      handleUpdate('date_service', convertDateToString(date));
                    }
                  }}
                />
                <img src={calendarIcon} alt="calendar icon" />
              </div>
            </div>
            <InputCardField
              id={`vehicle-decal-${greaseTrap.id}`}
              type="text"
              doNotUseSaveSelfButton
              disabled
              label={t('vendor-grease-trap-card.vehicle-decal')}
              selectedValue={d.vehicle_decal_number}
            />
            <div className="flex items-center justify-between gap-2 border-b border-gray-400">
              <div className="truncate text-sm !font-normal text-gray-400">
                {t('vendor-grease-trap-card.driver')}
              </div>
              <Dropdown
                options={dropdownOptions?.driverOptions || []}
                selectedValue={d.driver_id || ''}
                placeholder={t('vendor-grease-trap-card.driver')}
                onSelectChange={(newValue) => {
                  handleUpdate('driver_id', newValue);
                }}
                selectClassname="!font-normal"
              />
            </div>
          </div>
          <div className="flex w-full items-center text-base font-semibold text-zinc-800">
            {t('vendor-grease-trap-card.entry-title')}
          </div>
          <div className="grid w-full grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <InputCardField
              id={`gross-pounds-${greaseTrap.id}`}
              type="number"
              format="number"
              otherUnit="Lb"
              doNotUseSaveSelfButton
              label={t('vendor-grease-trap-card.gross-pounds')}
              selectedValue={d.gross_pounds}
              placeholder={d.gross_pounds}
              onSelectChange={(val) => {
                setValue('gross_pounds', Number(val));
              }}
              onSave={() => {
                handleUpdate('gross_pounds', d.gross_pounds);
              }}
            />
            <InputCardField
              id={`gallons-${greaseTrap.id}`}
              type="number"
              format="number"
              unitGal
              doNotUseSaveSelfButton
              label={t('vendor-grease-trap-card.gallons')}
              selectedValue={d.gallons_grease_received}
              placeholder={d.gallons_grease_received}
              onSelectChange={(val) => {
                setValue('gallons_grease_received', Number(val));
              }}
              onSave={() => {
                handleUpdate(
                  'gallons_grease_received',
                  d.gallons_grease_received,
                );
              }}
            />
            <InputCardField
              id={`container-volume-${greaseTrap.id}`}
              type="number"
              format="number"
              unitGal
              doNotUseSaveSelfButton
              disabled
              label={t('vendor-grease-trap-card.container-volume')}
              selectedValue={d.container_volume}
            />
            <InputCardField
              id={`manifest-${greaseTrap.id}`}
              type="text"
              doNotUseSaveSelfButton
              disabled
              label={t('vendor-grease-trap-card.manifest')}
              selectedValue={d.manifest_number}
            />
            <div className="flex items-center justify-between gap-2 border-b border-gray-400">
              <div className="truncate text-sm !font-normal text-gray-400">
                {t('vendor-grease-trap-card.registered-by')}
              </div>
              <Dropdown
                options={dropdownOptions?.userOptions || []}
                selectedValue={
                  (d.registered_by as unknown as { id: string })?.id || ''
                }
                placeholder={t('vendor-grease-trap-card.registered-by')}
                onSelectChange={(newValue) => {
                  handleUpdate('registered_by', newValue);
                }}
                selectClassname="!font-normal"
              />
            </div>
            <div className="flex w-full items-center justify-between gap-2 border-b border-gray-400">
              <Toggle
                onSelectChange={(newValue) => {
                  handleUpdate('traceability', newValue);
                }}
                selectedValue={d.traceability}
                placeholder={t('vendor-grease-trap-card.traceability')}
                className="w-full"
                placeholderClassName="!text-gray-400"
              />
            </div>
          </div>
          <div className="flex w-full items-center text-base font-semibold text-zinc-800">
            {t('vendor-grease-trap-card.pricing-title')}
          </div>
          <div className="grid w-full grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <InputCardField
              id={`amount-${greaseTrap.id}`}
              type="number"
              format="money"
              doNotUseSaveSelfButton
              disabled
              label={t('vendor-grease-trap-card.total-amount')}
              selectedValue={d.total_amount}
              placeholder={d.total_amount}
            />
            <InputCardField
              id={`balance-${greaseTrap.id}`}
              type="number"
              format="money"
              doNotUseSaveSelfButton
              disabled
              label={t('vendor-grease-trap-card.pending-balance')}
              selectedValue={d.balance}
              placeholder={d.balance}
            />
          </div>
          <div className="flex w-full items-center text-base font-semibold text-zinc-800">
            {t('vendor-grease-trap-card.payments')}
          </div>
          <div className="w-full">
            <div className="flex w-full flex-wrap gap-3">
              {d.payments &&
                d.payments.length > 0 &&
                d.payments.map((payment, index) => (
                  <GreaseTrapPaymentCard
                    key={payment.id}
                    payment={payment}
                    setValue={(key, value) => {
                      setValue(
                        `payments.${index}.${key}` as never,
                        value as never,
                      );
                    }}
                    onUpdatePayment={() => {
                      refetchSingleGreaseTrap(greaseTrap.id);
                      setNeedsRefresh('grease-traps-stats');
                      setTimeout(() => {
                        setNeedsRefresh(false);
                      }, 500);
                    }}
                  />
                ))}
              <button
                onClick={handleNewPayment}
                id="addGreaseTrapPayment"
                className="box-shadow flex h-auto min-h-[170px] w-[74px] items-center justify-center rounded-lg"
                type="button"
              >
                <img src={iconAdd} alt="add icon" />
              </button>
            </div>
          </div>
          <DropdownWithContent
            label="Documents"
            renderComponent={
              <DTGreaseTrapDocuments
                greaseTrapId={greaseTrap.id}
                vendorId={greaseTrap.vendor_id}
              />
            }
          />
          <DropdownWithContent
            label="Notes"
            renderComponent={
              <GreaseTrapNotes
                grease_trap_id={greaseTrap.id}
                vendor_id={greaseTrap.vendor_id}
              />
            }
          />
          <div className="flex w-full items-center justify-center">
            <div className="m-6 flex justify-center">
              <button
                onClick={handleDeleteGreaseTrap}
                id="removeGreaseTrap"
                className="flex items-center"
                type="button"
              >
                <img src={removeIcon} alt="remove icon" />
                <span className="ml-2 text-xs font-bold text-redish underline">
                  {t('vendor-grease-trap-card.delete-grease-trap')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GreaseTrapCard;
